import type { OrderIntermediatesStatus } from '../../../../Generated/OrderIntermediatesStatus.ts'
import type { IntermediatesTone } from '../../../../Util/IntermediatesLifetime.ts'
import { describeIntermediates, intermediatesExpired } from '../../../../Util/IntermediatesLifetime.ts'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'

const TOOLTIP =
  'Preprocessing intermediates are the files postprocessing is built from. They are deleted automatically ' +
  'a fixed time after the order was submitted to preprocessing, regardless of the order state.'

const bannerClass: Record<IntermediatesTone, string> = {
  neutral: 'alert',
  info: 'alert alert-info',
  warning: 'alert alert-warning',
  danger: 'alert alert-error'
}

const lineClass: Record<IntermediatesTone, string> = {
  neutral: 'text-neutral-600',
  info: 'text-info',
  warning: 'text-warning font-semibold',
  danger: 'text-error font-semibold'
}

interface NoticeProps {
  status: OrderIntermediatesStatus | null
  now?: Date
}

export const IntermediatesExpiredBadge = ({ status, now = new Date() }: NoticeProps) => {
  if (!intermediatesExpired(status, now)) return null
  return (
    <TooltipTrigger classNames={'inline-flex'} tooltip={TOOLTIP}>
      <span
        data-testid="intermediates-expired-badge"
        className="ml-2 inline-flex items-center bg-red-700 text-white text-sm font-semibold px-3 py-1 rounded-full"
      >
        Intermediates expired
      </span>
    </TooltipTrigger>
  )
}

/** The prominent deadline, for orders waiting on the postprocessing decision. */
export const IntermediatesBanner = ({ status, now = new Date() }: NoticeProps) => {
  if (!status) return null
  const description = describeIntermediates(status, now)
  if (description.state === 'NOT_STARTED') return null
  return (
    <div
      role="status"
      data-testid="intermediates-banner"
      data-state={description.state}
      className={`${bannerClass[description.tone]} my-2 py-2 text-sm`}
    >
      <span>{description.text}</span>
    </div>
  )
}

/** The same information as one line, for the pipeline-information modal. */
export const IntermediatesLine = ({ status, now = new Date() }: NoticeProps) => {
  if (!status) return null
  const description = describeIntermediates(status, now)
  return (
    <p data-testid="intermediates-line" data-state={description.state} className={`text-sm mb-2 ${lineClass[description.tone]}`}>
      {description.text}
    </p>
  )
}
